import {
  Box,
  Icon,
  Link,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Heading,
  UnorderedList,
  ListItem,
  Container,
  Grid,
  GridItem,
  Stack,
  Text,
  Flex,
  Modal,
  useDisclosure,
  ModalOverlay,
  ModalContent,
  ModalBody,
} from '@everlywell/ui-kit';
import { House } from '@phosphor-icons/react';
import Carousel from 'components/Carousel';
import { useCart } from 'components/Cart';
import ProductTag from 'components/PageSections/components/ProductTag';
import { OptionsValues } from 'components/PrePurchaseScreener';
import PrePurchaseWrapper from 'components/PrePurchaseWrapper';
import { GatsbyImage } from 'gatsby-plugin-image';
import debounce from 'lodash/debounce';
import Markdown from 'markdown-to-jsx';
import React, { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Zoom } from 'swiper/modules';
import { SwiperClass } from 'swiper/react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { ContentfulRichText } from 'utils/contentful/renderRichText';
import {
  discountedPrice,
  findActivePromotionForProduct,
} from 'utils/helpers/activePromoHelpers';
import { QUEST_TAG } from 'utils/helpers/products';
import useAdvertisedPromos from 'utils/hooks/useAdvertisedPromos';
import useProductCategories from 'utils/hooks/useProductCategories';
import { ProductCategory } from 'utils/types';
import { notEmpty } from 'utils/types/utilityTypes';

import HeroSubsection from './components/HeroSubsection';
import MobilePurchaseOptions from './components/MobilePurchaseOptions';
import PurchaseOptions from './components/PurchaseOptions';
import QuestLocationFinder from './components/QuestLocationFinder';
import ReferAFriendEntryPoint from './components/ReferAFriendEntryPoint/ReferAFriendEntryPoint';
import StickyAddToCartBar from './components/StickyAddToCartBar';
import {
  SelectedOptionContext,
  PurchaseSelections,
  PurchaseOptionsContextType,
} from './SelectedOption.context';

export type PDPHeroProps = {
  heroData: Queries.pdpHeroFragment;
  product: Queries.pdpProductFragment;
  screenerData?: Queries.pdpScreenerFragment;
  disclaimerData?: Queries.pdpDisclaimerFragment;
  isLandingPage?: boolean;
  contentfulFlags?: Queries.pdpContentfulMetadata001Fragment['flags'];
  whatIsMeasuredData: Queries.PDPQuery['whatsMeasured']['nodes'][0];
};

const hasContentfulFlag = (
  flagId: string,
  contentfulFlags: PDPHeroProps['contentfulFlags'],
) =>
  Boolean(
    contentfulFlags?.find(
      (flag) => flag?.metadata?.metadata_id === flagId && flag?.value === true,
    ),
  );

/**
 * [PDP Hero].
 */
const PDPHero = (props: PDPHeroProps) => {
  const { inView, ref, entry } = useInView({
    initialInView: false,
    threshold: 0,
    rootMargin: '-68px 0px 0px 0px',
  });

  const { addItemToCartAsync } = useCart();

  const heroRef = useRef<HTMLDivElement>(null);

  const {
    heroData,
    product: productData,
    screenerData,
    disclaimerData,
    isLandingPage,
    contentfulFlags,
  } = props;

  const { spreeProduct } = productData;

  const isQuestProduct = hasContentfulFlag('isQuestProduct', contentfulFlags);
  const skipPreCart = hasContentfulFlag('skipPreCart', contentfulFlags);
  const hidePaymentOptions = hasContentfulFlag(
    'hidePaymentOptions',
    contentfulFlags,
  );

  const hasPrePurchaseStep = !!(screenerData && disclaimerData);

  const screenerOptions = screenerData?.options?.map((option) => ({
    items:
      option?.items?.filter((item): item is string => item !== null) ?? null,
    label: option?.displayName ?? '',
    value:
      option?.eligibility?.value === true
        ? OptionsValues.eligible
        : OptionsValues.ineligible,
  }));

  const screener = {
    title: screenerData?.displayName || '',
    description: screenerData?.metadata?.description?.description || '',
    callToActionText: screenerData?.metadata?.ctas?.[0]?.label || '',
    options: screenerOptions || null,
  };

  const disclaimer = {
    title: disclaimerData?.title || '',
    body: disclaimerData?.body as ContentfulRichText,
    callToAction: {
      text: disclaimerData?.callToAction?.text || '',
      url: disclaimerData?.callToAction?.url || '',
    },
  };

  const preSelectedSubscriptionFlag = props.product.metadata?.flags
    ?.filter(notEmpty)
    .find((flag) => flag.metadata?.metadata_id === 'preSelectedSubscription');
  const isPreSelectedSubscription = preSelectedSubscriptionFlag?.value === true;

  const { onClose } = useDisclosure();

  const handleModalClose = () => {
    setIsModalOpen(false);
    onClose();
  };

  // ### Subscription
  const subscriptionVariants = spreeProduct?.variants?.filter(
    (variant) => variant?.subscription_plan,
  );

  const subscriptionOptions =
    subscriptionVariants
      ?.map((variant) => {
        if (!variant || !variant.subscription_plan) return null;
        return {
          id: variant?.id ?? 0,
          name: variant?.subscription_plan?.name ?? '',
          unit: variant?.subscription_plan?.interval ?? '',
          price: variant?.price ?? '0',
          percentDiscount: variant?.subscription_plan?.percent_discount ?? 0,
        };
      })
      .filter(Boolean) ?? [];
  const singleSubscriptionOption = subscriptionOptions[0];
  const hasSubscriptionOptions = !!singleSubscriptionOption;

  const [selectedOption, setSelectedOption] = useState<PurchaseSelections>(
    hasSubscriptionOptions && isPreSelectedSubscription
      ? 'subscription'
      : 'oneTimePurchase',
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [initialSlide, setInitialSlide] = useState(0);

  const selectedOptionsValue: PurchaseOptionsContextType = {
    selectedOption,
    setSelectedOption,
  };

  // ### Promotions
  const { advertisedPromos } = useAdvertisedPromos();
  const activePromo =
    findActivePromotionForProduct(
      spreeProduct?.productId ?? 0,
      advertisedPromos,
    ) || null;

  const productVariant = spreeProduct?.variants?.find(
    (variant) => variant?.id === `${spreeProduct?.master_variant_id ?? 0}`,
  );

  const discountedPromoPrice = parseFloat(
    discountedPrice(
      productVariant?.price ?? '0',
      activePromo ? activePromo : undefined,
    ),
  );

  const isPriceDiscounted =
    discountedPromoPrice < parseFloat(productVariant?.price ?? '0');

  /**
   * If product price after promo applied is 0, so a promo of 0%, do not show
   * the promo and strikethrough.
   * This is done to allow tiered promos that start with a range of 0% discount.
   */
  const showPromo = Boolean(
    activePromo && spreeProduct?.promotionable && isPriceDiscounted,
  );

  const { productCategoryMap } = useProductCategories();

  const productCategory = Object.values(
    productCategoryMap as Record<string, ProductCategory>,
  ).find((category) =>
    category.product?.some((product) => product.slug === productData.slug),
  );

  const trackBreadcrumbClick = (name: string) => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_LINK,
      data: {
        label: ANALYTICS.LABELS.BREADCRUMB,
        text: name,
      },
    });
  };

  const trackZoomImageClick = () => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_LINK,
      data: {
        label: ANALYTICS.PDP.ZOOM_IMAGE,
        slug: productData.slug || '',
      },
    });
  };

  /**
   * Debounced function to track carousel interactions.
   * This is needed to avoid tracking too many events in a short period of time.
   */
  const trackCarouselInteractionRef = useRef(
    debounce(
      (payload: {
        action: string;
        activeIndex: number;
        isModalOpen: boolean;
      }) => {
        analytics.track({
          event: ANALYTICS.EVENTS.CLICKED_BUTTON,
          data: {
            label: 'PDP - Carousel Interaction',
            slug: productData.slug || '',
            name: spreeProduct?.name ?? '',
            productId: spreeProduct?.productId ?? '',
            ...payload,
          },
        });
      },
      300,
    ),
  );

  // clear the debounced function on unmount
  useEffect(
    () => () => {
      trackCarouselInteractionRef.current.cancel();
    },
    [],
  );

  if (!heroData || !spreeProduct) return null;

  const { images, metadata, sections } = heroData;

  const questImages = images?.filter(
    (image) =>
      image?.desktopImageAltText?.toLowerCase().includes('quest') ||
      image?.mobileImageAltText?.toLowerCase().includes('quest'),
  );

  const descriptions = metadata?.description?.description?.split('\n');
  const { name } = spreeProduct;

  const isProductOutOfStock =
    props.product.metadata?.flags
      ?.filter(notEmpty)
      .find((flag) => flag.metadata?.metadata_id === 'outOfStock')?.value ===
    true;
  const showStickyAddToCartBar = entry && !inView && !isProductOutOfStock;

  // ### PrePurchaseWrapper functions

  const variantId =
    selectedOption === 'subscription'
      ? Number(singleSubscriptionOption?.id ?? '0')
      : spreeProduct?.master_variant_id;

  const handleAddToCartClick = async () => {
    if (variantId) {
      addItemToCartAsync({
        variant_id: variantId,
        quantity: 1,
      });
    }
  };

  // Element to render the images swiper
  // This is the main image gallery for the product
  // To be used twice in this file, once for the main image gallery and once for the modal
  const imagesSwiper = images ? (
    <>
      <Box
        borderRadius={'12px'}
        flexShrink={0}
        paddingX={[isModalOpen ? 0 : 4, 4]}
        paddingY={3}
        marginTop={[isModalOpen ? 0 : 6, null, 8]}
        backgroundColor={'tints.lightCream'}
        zIndex={0}
        sx={{
          '& .swiper-wrapper': {
            alignItems: 'stretch',
          },
          '& .swiper-slide': {
            height: isModalOpen ? 'calc(70vh)' : 'auto',
            display: isModalOpen ? 'flex' : 'block',
            alignItems: 'center',
          },

          // constrains the image to the container
          '.gatsby-image-wrapper-constrained': {
            maxHeight: '400px',

            '@media screen and (max-width: 768px)': {
              maxHeight: '236px',
            },
          },
        }}
      >
        <Carousel
          pagination={true}
          slidesPerView={1}
          showPagination
          modules={[Zoom]}
          zoom={true}
          arrowPosition="sides"
          initialSlide={initialSlide}
          onSlidePrevTransitionEnd={(swiper: SwiperClass) => {
            trackCarouselInteractionRef.current({
              action: 'previous',
              activeIndex: swiper.activeIndex,
              isModalOpen,
            });
          }}
          onSlideNextTransitionEnd={(swiper: SwiperClass) => {
            trackCarouselInteractionRef.current({
              action: 'next',
              activeIndex: swiper.activeIndex,
              isModalOpen,
            });
          }}
          onZoomChange={(swiper: SwiperClass) => {
            trackCarouselInteractionRef.current({
              action: 'zoomed',
              activeIndex: swiper.activeIndex,
              isModalOpen,
            });
          }}
        >
          {images?.map((image, index) => {
            const desktopImage = image?.desktopImage?.gatsbyImageData;
            const desktopAltText = image?.desktopImageAltText || '';
            const mobileImage =
              image?.mobileImage?.gatsbyImageData ?? desktopImage;
            const mobileAltText = image?.mobileImageAltText || desktopAltText;

            return (
              <Box
                key={`image-${index}`}
                onClick={() => {
                  setInitialSlide(index);
                  setIsModalOpen(true);
                  trackZoomImageClick();
                }}
              >
                {mobileImage && (
                  <Box
                    display={['block', 'none']}
                    // Class required by swiper for the zoom effect
                    className="swiper-zoom-container"
                  >
                    <GatsbyImage
                      image={mobileImage}
                      alt={mobileAltText}
                      objectFit="contain"
                      loading={index === initialSlide ? 'eager' : 'lazy'}
                      // Class required by swiper for the zoom effect
                      class="swiper-zoom-target"
                    />
                  </Box>
                )}
                {desktopImage && (
                  <Box
                    display={['none', 'block']}
                    _hover={{
                      cursor: !isModalOpen ? 'pointer' : 'default',
                    }}
                  >
                    <GatsbyImage
                      image={desktopImage}
                      alt={desktopAltText}
                      objectFit="contain"
                      loading={index === initialSlide ? 'eager' : 'lazy'}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
        </Carousel>
      </Box>
      <ReferAFriendEntryPoint />
    </>
  ) : null;

  return (
    <SelectedOptionContext.Provider value={selectedOptionsValue}>
      <PrePurchaseWrapper addToCart={handleAddToCartClick} />
      <Container size={['sm', null, 'xl']} marginX="auto">
        <Grid
          spacing={[4, 10, 12, 20]}
          ref={heroRef}
          display={'flex'}
          flexDirection={['column-reverse', 'column-reverse', 'row']}
        >
          {/* sticky add to cart bar */}
          <StickyAddToCartBar
            showPromo={showPromo}
            activePromo={activePromo}
            product={productData}
            heroRef={heroRef}
            isVisible={showStickyAddToCartBar}
            hasPrePurchaseStep={hasPrePurchaseStep}
            screener={screener}
            disclaimer={disclaimer}
            isNavbarVisible={!isLandingPage}
            isQuestProduct={isQuestProduct}
            skipPreCart={skipPreCart}
          />
          {/* left side */}
          <GridItem position={'relative'} width={[1, null, '55%']}>
            <Box
              position={'absolute'}
              top={0}
              left={0}
              zIndex={1}
              marginTop={5}
              marginLeft={[4, 12]}
              display="none"
            >
              <Breadcrumb
                separator={'·'}
                spacing={1}
                color="tints.darkGrey"
                fontSize={'sm'}
                fontWeight={'normal'}
              >
                <BreadcrumbItem>
                  <BreadcrumbLink href="/">
                    <Icon
                      as={House}
                      color={'viridian.base'}
                      display={'flex'}
                      alignItems={'center'}
                      weight="fill"
                      size={'sm'}
                    />
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbItem>
                  {productCategory && (
                    <BreadcrumbLink
                      href={`/products/?category=${productCategory.pipId}`}
                      color={'viridian.base'}
                      onClick={() =>
                        trackBreadcrumbClick(productCategory.title)
                      }
                    >
                      {productCategory.title}
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
              </Breadcrumb>
            </Box>

            <Stack spacing={4}>
              {isQuestProduct ? (
                <QuestLocationFinder backgroundImages={questImages} />
              ) : (
                imagesSwiper
              )}
              {/* show subsections below the image on the left on desktop or larger */}
              {sections && sections.length > 0 && (
                <Box>
                  <Stack>
                    {sections.map((section) =>
                      section ? (
                        <HeroSubsection
                          key={section.contentful_id}
                          data={section}
                          slug={productData.slug || ''}
                          product={productData}
                          whatIsMeasuredData={props.whatIsMeasuredData}
                        />
                      ) : null,
                    )}
                  </Stack>
                </Box>
              )}
            </Stack>
          </GridItem>
          {/* right side */}
          <GridItem width={[1, null, '45%']}>
            <Box
              paddingLeft={{
                desktop: 0,
              }}
              marginTop={[10, 8]}
              gap={2}
              sx={{
                position: 'sticky',
                top: '70px', // 70px is the calculated from the nav header and the heading top margin
                display: 'flex',
                flexDirection: 'column',
              }}
              className="ju_forcepush"
            >
              {isLandingPage ? (
                <>
                  <Text
                    color="viridian.base"
                    size="lg"
                    marginBottom={2}
                    textAlign={['center', 'left']}
                  >
                    {metadata?.context?.eyebrow ?? name}
                  </Text>
                  <Heading size="xl" textAlign={['center', 'left']} as="h1">
                    {metadata?.context?.title ?? ''}
                  </Heading>
                </>
              ) : isQuestProduct ? (
                <Flex gap={6} flexDirection="column" marginTop={[0, 10]}>
                  <Flex justifyContent={['center', 'left']}>
                    <ProductTag displayText={QUEST_TAG} />
                  </Flex>
                  <Heading size="xl" textAlign={['center', 'left']} as="h1">
                    {name}
                  </Heading>
                </Flex>
              ) : (
                <Heading
                  size="xl"
                  marginTop={[0, 10]}
                  textAlign={['center', 'left']}
                  as="h1"
                >
                  {name}
                </Heading>
              )}

              {metadata && descriptions ? (
                <UnorderedList
                  fontSize={'md'}
                  display={{ base: 'none', desktop: 'block' }}
                >
                  {descriptions.map((description, index) => (
                    <ListItem key={index} fontWeight={'normal'}>
                      <Markdown
                        options={{
                          overrides: {
                            a: {
                              component: Link,
                            },
                          },
                        }}
                      >
                        {description}
                      </Markdown>
                    </ListItem>
                  ))}
                </UnorderedList>
              ) : null}

              {/* render the payment options here */}
              {props.product.spreeProduct && (
                <Box ref={ref}>
                  <Box display={{ base: 'none', tablet: 'block' }}>
                    <PurchaseOptions
                      product={props.product.spreeProduct}
                      isProductOutOfStock={isProductOutOfStock}
                      hasPrePurchaseStep={hasPrePurchaseStep}
                      screener={screener}
                      disclaimer={disclaimer}
                      isQuestProduct={isQuestProduct}
                      hidePaymentOptions={hidePaymentOptions}
                      contentfulFlags={contentfulFlags}
                      showPromo={showPromo}
                      activePromo={activePromo}
                    />
                  </Box>
                  <Box display={{ base: 'block', tablet: 'none' }}>
                    <MobilePurchaseOptions
                      product={props.product.spreeProduct}
                      isProductOutOfStock={isProductOutOfStock}
                      isPreSelectedSubscription={isPreSelectedSubscription}
                      hasPrePurchaseStep={hasPrePurchaseStep}
                      screener={screener}
                      disclaimer={disclaimer}
                      isQuestProduct={isQuestProduct}
                      skipPreCart={skipPreCart}
                      contentfulFlags={contentfulFlags}
                      showPromo={showPromo}
                      activePromo={activePromo}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </GridItem>
        </Grid>
      </Container>
      {isModalOpen && (
        <Box
          sx={{
            '@media screen and (max-width: 600px)': {
              '.pdp-modal-zoom > div': {
                paddingTop: '0',

                '> div': {
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0',
                },
              },
            },
          }}
        >
          <Box className="pdp-modal-zoom">
            <Modal isOpen={isModalOpen} onClose={handleModalClose} size="xl">
              <ModalOverlay />
              <ModalContent>
                <ModalBody>
                  <Box width="100%" height="fit-content">
                    {imagesSwiper}
                  </Box>
                </ModalBody>
              </ModalContent>
            </Modal>
          </Box>
        </Box>
      )}
    </SelectedOptionContext.Provider>
  );
};

export default PDPHero;
